import { useQuery } from "@tanstack/react-query";
import { FeatureFlagAPI } from "@/utils/axios";

const STALE_TIME = 60 * 1000; // 1 minute - balanced for feature flags
const GC_TIME = 5 * 60 * 1000; // 5 minutes

export const FEATURE_FLAGS = {
  ENABLE_PROVIDER_CREDENTIALS: "ENABLE_PROVIDER_CREDENTIALS",
  CLOUD_BUILDER: "CLOUD_BUILDER",
  ENABLE_MIGRATION_AGENT: "ENABLE_MIGRATION_AGENT",
  ENABLE_API_KEY: "ENABLE_API_KEY",
} as const;

/**
 * Default values for feature flags during loading states.
 * This ensures consistency across the application.
 *
 * Security-critical features should default to false.
 * UI/UX features that prevent layout shift can default to true.
 */
export const FEATURE_FLAG_DEFAULTS: Record<string, boolean> = {
  [FEATURE_FLAGS.ENABLE_PROVIDER_CREDENTIALS]: false, // Security feature - default to false
  [FEATURE_FLAGS.CLOUD_BUILDER]: true, // UI feature - default to true to prevent flashing
  [FEATURE_FLAGS.ENABLE_MIGRATION_AGENT]: true, // UI feature - default to true to prevent flashing
  [FEATURE_FLAGS.ENABLE_API_KEY]: true,
};

export const useFeatureFlags = () => {
  return useQuery({
    queryKey: ["featureFlags"],
    queryFn: () => FeatureFlagAPI.getFeatureFlag(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 1, // Reduce retries for faster feedback
    retryDelay: 500, // Faster retry
    refetchOnWindowFocus: false, // Reduce API calls
    refetchOnReconnect: true,
    refetchInterval: false, // Disable polling - rely on stale time instead
    meta: {
      timeout: 5000, // Faster timeout
    },
  });
};

export const useFeatureFlag = (flagName: string) => {
  const { data: flags, isLoading, error } = useFeatureFlags();

  return {
    isEnabled: flags?.[flagName] ?? false,
    isLoading,
    error,
  };
};

/**
 * Hook for UI elements that need to prevent layout shift during loading.
 * Use this ONLY for non-security-critical UI elements.
 *
 * @param flagName - The feature flag name to check
 * @param defaultValue - Default value during loading (uses centralized defaults if not provided)
 * @returns Object with isEnabled, isLoading, error, and isUsingDefault
 *
 * @example
 * // Uses centralized default for CLOUD_BUILDER (true)
 * const { isEnabled } = useFeatureFlagWithDefault(FEATURE_FLAGS.CLOUD_BUILDER);
 *
 * // Override default for specific use case
 * const { isEnabled } = useFeatureFlagWithDefault('SHOW_BANNER', false);
 */
export const useFeatureFlagWithDefault = (
  flagName: string,
  defaultValue?: boolean
) => {
  const { data: flags, isLoading, error } = useFeatureFlags();

  // Use provided default or fall back to centralized defaults, then false
  const effectiveDefault =
    defaultValue ?? FEATURE_FLAG_DEFAULTS[flagName] ?? false;

  return {
    isEnabled: isLoading ? effectiveDefault : flags?.[flagName] ?? false,
    isLoading,
    error,
    isUsingDefault: isLoading,
  };
};
